import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useAuthenticationStore } from '@/features/auth/stores/authenticationStore.js';
import { useWorkspaceStore } from '@/features/workspaces/stores/workspaceStore.js';
import { useClient } from '@/plugins/api.js';

export const useContentItemsStore = defineStore('content-items', () => {
    const auth = useAuthenticationStore();
    const workspaceStore = useWorkspaceStore();
    const client = useClient();

    const items = ref([]);
    const isLoading = ref(false);
    const isCreating = ref(false);
    const isMoving = ref(false);
    const error = ref(null);

    const activeCount = computed(
        () => items.value.filter(item => !['Approved', 'Scheduled', 'Published'].includes(item.status)).length
    );

    async function fetchContentItems(filters = {}) {
        if (!auth.isAuthenticated) {
            items.value = [];
            error.value = null;
            return;
        }

        isLoading.value = true;
        error.value = null;

        try {
            const response = await client.get('/api/content-items', {
                params: {
                    workspaceId: workspaceStore.activeWorkspaceId ?? undefined,
                    campaignId: filters.campaignId,
                },
            });

            items.value = response.data ?? [];
        } catch (fetchError) {
            console.error('Failed to fetch content items:', fetchError);
            items.value = [];
            error.value = 'Failed to load content items.';
        } finally {
            isLoading.value = false;
        }
    }

    async function createContentItem(payload) {
        const workspaceId = payload.workspaceId ?? workspaceStore.activeWorkspaceId;

        if (!auth.isAuthenticated || !workspaceId) {
            throw new Error('You must be authenticated to create a content item.');
        }

        if (isCreating.value) {
            throw new Error('A content item creation request is already in progress.');
        }

        isCreating.value = true;
        error.value = null;

        try {
            const response = await client.post('/api/content-items', {
                ...payload,
                workspaceId,
            });

            if (response.data) {
                items.value = [response.data, ...items.value];
            }

            return response.data;
        } catch (createError) {
            console.error('Failed to create content item:', createError);
            error.value = 'Failed to create content item.';
            throw createError;
        } finally {
            isCreating.value = false;
        }
    }

    async function fetchContentItem(id) {
        const response = await client.get(`/api/content-items/${id}`);
        return response.data;
    }

    async function moveContentItemSchedule(id, plannedPublishDate) {
        if (!auth.isAuthenticated) {
            throw new Error('You must be authenticated to move a content item.');
        }

        if (isMoving.value) {
            throw new Error('A content item schedule move is already in progress.');
        }

        isMoving.value = true;
        error.value = null;

        try {
            const response = await client.patch(`/api/content-items/${id}/schedule`, {
                plannedPublishDate,
            });
            const updated = response.data;

            if (updated?.id) {
                items.value = items.value.map(item =>
                    item.id === updated.id
                        ? {
                              ...item,
                              ...updated,
                              collaborators: updated.collaborators?.length ? updated.collaborators : item.collaborators,
                          }
                        : item
                );
            }

            return updated;
        } catch (moveError) {
            console.error('Failed to move content item schedule:', moveError);
            error.value = 'Failed to move content item.';
            throw moveError;
        } finally {
            isMoving.value = false;
        }
    }

    watch(
        () => [auth.isAuthenticated, workspaceStore.activeWorkspaceId],
        async ([isAuthenticated]) => {
            if (!isAuthenticated) {
                items.value = [];
                error.value = null;
                return;
            }

            await fetchContentItems();
        },
        { immediate: true }
    );

    return {
        items,
        isLoading,
        isCreating,
        isMoving,
        error,
        activeCount,
        fetchContentItems,
        fetchContentItem,
        createContentItem,
        moveContentItemSchedule,
    };
});
